// The pure markup helpers of the DOM overlay: each one turns its arguments
// into a string of HTML, or into one element, and reads no field of the
// `Overlay` class. A helper that two menus share sits here, so one thing
// reads the same in both.

import type { Job, LevelEffect, LevelUnlock, SeaObjectKind, ShipClass, SiegeInfo } from '../../gen/proto/aquaphobia';
import { JobKind, LevelEffectKind, LevelUnlockKind, ObjKind } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { pad2 } from '../../locale/stamp';
import { type GameState, MISSION_NAMES, dur } from '../../state';
import { esc } from '../esc';
import type { EffectRow, Fact } from '../cards/card';
import { mixLine, type Stock } from '../rounds';
import { tabLabel } from './tables';
import type { UIFleet } from './types';
import { groupThousands } from '../../locale/group';

/** One sub-menu row (R258): the sections of a menu, or the pages of the
 *  auction. Each press is a sub-menu button, which goes to another page;
 *  it is no pill, because a pill sets a saved value (R2011). */
export const subRow = (act: string, keys: readonly string[], cur: string): string =>
  '<div class="subrow">' + keys.map((x) =>
    `<button class="sub${x === cur ? ' on' : ''}" data-act="${act}" data-sub="${x}">${tabLabel(x)}</button>`).join('')
  + '</div>';

/** What a penned hull is prepared with: who is aboard and what fills the
 * tubes. Line 2 of a hull in the pens in FLEET · MISSIONS states it (R1888).
 * The ship class is not repeated here — the row already carries it. The
 * load is the saved mix of the next deploy, `12 HARPOON` for one kind and
 * `6H · 3R` for more, or `auto` (R2031). */
export function prepLine(
  s: GameState,
  st: { captainId: number; loadMix: readonly Stock[]; cls: ShipClass },
  support: boolean,
  cap: { name: string } | undefined,
): string {
  // Names only, no labels. The row already says what each slot is — the
  // buttons beside it are CAPTAIN and LOAD — so prefixing every value with
  // "capt" and "tubes" spent the width that keeps this to one line.
  const parts: string[] = [];
  if (!support) parts.push(cap ? esc(cap.name) : S.prep_no_captain);
  if ((s.ship(st.cls)?.torpedoes.length ?? 0) > 0) {
    const mix = mixLine(st.loadMix, (k) => s.torpedo(k)?.name ?? '?');
    parts.push(mix ? esc(mix) : S.prep_auto);
  }
  return parts.join(' · ');
}

/** The word of each module grade code (numbers.md §6, R1194), built once
 * at load because the language is picked once (R855). */
const GRADE_WORDS: Record<string, string> = {
  flawed: S.grade_flawed, standard: S.grade_standard, fine: S.grade_fine,
  superb: S.grade_superb, perfect: S.grade_perfect,
};

/** The word of a module grade. The pack names the codes and the locale
 * owns the words; a code this build has no word for prints as itself, so
 * a patched pack never blanks a row. */
export function moduleGradeWord(code: string): string {
  return GRADE_WORDS[code] ?? code;
}

/** A fitted module as a row prints it: the name with its rolled effect,
 * then the grade of the roll when it has one (R1194). Escaped. */
export function moduleWithGrade(m: { name: string; grade: string }): string {
  return m.grade
    ? S.module_graded({ module: esc(m.name), grade: esc(moduleGradeWord(m.grade)) })
    : esc(m.name);
}

/** The glyph of each grade code on the fit strip (R1468). A Standard roll,
 * an empty code and a code this build does not know print no glyph. */
const FIT_GRADE_GLYPHS: Record<string, string> = {
  flawed: S.fit_grade_flawed, fine: S.fit_grade_fine,
  superb: S.fit_grade_superb, perfect: S.fit_grade_perfect,
};

/** A mark of a wire-enum list that counts from 1; 0 and an unknown value
 * give no mark. */
const markOf = (marks: readonly string[], v: number): string => (v > 0 ? marks[v - 1] ?? '' : '');

/** One mark of the fit strip in its colour class (R1477): `fw` the weapon
 * type, `fs` the shield type, `g-<code>` a module of that grade. */
const markSpan = (cls: string, mark: string): string =>
  mark ? `<span class="${cls}">${esc(mark)}</span>` : '';

/**
 * A fitted module as two lines in the colour of its grade, the colour the
 * fit strip of a pen row gives the same module (R1477, R1570, R2739): the
 * catalog name with the tier numeral, and the rolled effect with the
 * grade word. The REDRAFT rows of DRAFT · MASTERS print the slot on line 1
 * and the roll on line 2, so a narrow row never cuts the figure. `name`
 * is the catalog name of the family; `effectPct` is x10 percent.
 */
export function moduleLinesInGrade(
  m: { tier: number; effectPct: number; grade: string }, name: string,
): { slot: string; roll: string } {
  const code = gradeCode(m.grade);
  const pct = (m.effectPct / 10).toFixed(1);
  const roll = m.grade
    ? S.module_roll_graded({ pct, grade: esc(moduleGradeWord(m.grade)) })
    : S.module_roll({ pct });
  const slot = S.module_slot({ module: esc(name), tier: S.module_tiers[m.tier - 1] ?? String(m.tier) });
  return {
    slot: `<span class="gmod g-${code}">${slot}</span>`,
    roll: `<span class="gmod g-${code}">${roll}</span>`,
  };
}

/** The grade code a module mark takes its colour from. A code this build
 *  does not know takes the Standard colour, as it takes no glyph. */
function gradeCode(grade: string): string {
  return FIT_GRADE_GLYPHS[grade] ? grade : 'standard';
}

/**
 * The fit strip of a pen row (R1468): one short mark per fitted system,
 * the weapon type, the shield type, then the modules in module family
 * order, for example `RS BV WP3+ SH2 SN1`. Each mark takes the colour of
 * its kind, and a module mark the colour of its grade (R1477). Its title
 * holds the full type names and each module with its effect and grade. A
 * basic hull (no module, no type) gives an empty string. The fleet row
 * of a hull in the pens, the library rows of DRAFT (R1888) and the
 * systems fact of a fleet card all call it, so they read the same.
 */
export function fitStrip(
  names: { weaponTypes: readonly string[]; shieldTypes: readonly string[] } | null | undefined,
  st: {
    weapType: number; shieldType: number;
    fit: readonly { family: number; tier: number; name: string; grade: string }[];
  },
): string {
  const mods = [...st.fit].sort((a, b) => a.family - b.family);
  const marks = [
    markSpan('fw', markOf(S.fit_weapon_marks, st.weapType)),
    markSpan('fs', markOf(S.fit_shield_marks, st.shieldType)),
  ];
  for (const m of mods) {
    const family = markOf(S.fit_family_marks, m.family);
    if (family) {
      marks.push(markSpan(`g-${gradeCode(m.grade)}`,
        S.fit_module({ family, tier: m.tier, grade: FIT_GRADE_GLYPHS[m.grade] ?? '' })));
    }
  }
  const html = marks.filter(Boolean).join(' ');
  if (!html) return '';
  const title = [
    st.weapType ? esc(names?.weaponTypes[st.weapType] ?? '') : '',
    st.shieldType ? esc(names?.shieldTypes[st.shieldType] ?? '') : '',
    ...mods.map(moduleWithGrade),
  ].filter(Boolean).join(' · ');
  return `<span class="fit" title="${title}">${html}</span>`;
}

/**
 * The wait of the SHIELD vital of the fleet card (R1503): the seconds
 * until the shield regen starts, in world time like the scan bar, or 0.
 * It stands only while the shield is below full and the server states a
 * wait; the card writes it as a short count (R1853). The server computes
 * the wait; the client mirrors neither the calm gate nor R2.
 */
export function regenWaitSec(s: GameState, f: Pick<UIFleet, 'regenInSec' | 'shieldPct'>): number {
  return regenWait(s.mapToWorldSec(f.regenInSec), f.shieldPct);
}

/**
 * The regen wait of `regenWaitSec` for a wait that is already on the
 * clock the card prints. The arcade corner prints wall seconds (R267),
 * so the match converts the wait and calls this half (R1504).
 */
export function regenWait(leftSec: number, shieldPct: number): number {
  return leftSec > 0 && shieldPct < 100 ? leftSec : 0;
}

/**
 * The noise figure of the speed row (R1500): the server's live noise in
 * thousandths, and the class figure `ShipSpec.noise` only when the field
 * reads 0, which means "not sent". The fleet window and the arcade corner
 * both call it (R1504).
 */
export function liveNoise(noiseMilli: number, classNoise: number): number {
  return noiseMilli > 0 ? noiseMilli / 1000 : classNoise;
}

/**
 * What the mission row says for one own hull.
 *
 * The table is the wire's own enum order, so it has one word for the whole
 * attack-move. A fleet with a focus does something narrower — it chases one
 * named hostile and fires at it alone (R544) — so the label states that
 * instead. The override sits after the lookup, so the table keeps its shape.
 * A hull the auto-withdraw turned for home reads WITHDRAWING and not the
 * Return word, so the rule's turn never reads as an own order (R3261).
 */
export function missionLabel(f: UIFleet): string {
  if (f.withdrawing) return S.mission_withdrawing;
  if (f.focus !== 0) return S.mission_attacking;
  return MISSION_NAMES[f.mission] ?? '—';
}

/**
 * The name of a dive at one depth, as the hello catalog gives it (R737).
 *
 * A prep job in the build queue carries the depth in its `count` field, and
 * the wardroom card and FLEET · MISSIONS both name the dive from it. The
 * lookup sits here so the two read one name. The caller escapes the text.
 */
export function diveName(s: GameState, depth: number): string {
  return diveSpec(s, depth)?.name ?? '?';
}

/** The catalog row of the dive at one depth, or undefined (R737). */
export function diveSpec(s: GameState, depth: number) {
  return s.catalog?.expeditions.find((x) => x.depth === depth);
}

/**
 * The prep job of the dive a captain outfits for, or undefined (R737,
 * R738). The job names the captain as its target and the depth as its
 * count; the server refuses every other order of that captain.
 */
export function outfitJob(s: GameState, captainId: number): Job | undefined {
  return s.queue.find((j) => j.kind === JobKind.JOB_KIND_EXPEDITION && j.target === captainId);
}

/**
 * The order of WAR · STRUCTURES: the Throne first, then the Spires, then
 * every other kind in alphabetical order of its name. Inside one kind the
 * order follows the site name. The function returns a new array.
 */
export function sortStructures(list: readonly SiegeInfo[]): SiegeInfo[] {
  const rank = (g: SiegeInfo): number =>
    g.kind === ObjKind.OBJ_KIND_THRONE ? 0 : g.kind === ObjKind.OBJ_KIND_SPIRE ? 1 : 2;
  return [...list].sort((a, b) =>
    rank(a) - rank(b)
    || (rank(a) === 2 ? a.kindName.localeCompare(b.kindName) : 0)
    || a.siteName.localeCompare(b.siteName));
}

/**
 * The place a strategic object stands at, without its kind.
 *
 * The site's wire name is "Kind · Place". WAR · STRUCTURES prints the kind
 * on its own line and FLEET · MISSIONS has no room for it, so both menus
 * take the place alone. The rule sits here, because one name must read the
 * same in the two menus (R733). The card of the object takes it as its
 * title, and its kind line states the kind once (R1879). The caller
 * escapes the text.
 */
export function sitePlace(g: Pick<SiegeInfo, 'siteName' | 'kindName'>): string {
  const kindPrefix = `${g.kindName} · `;
  return g.siteName.startsWith(kindPrefix) ? g.siteName.slice(kindPrefix.length) : g.siteName;
}

/**
 * The time to a strategic object's next vulnerability window, as the two
 * menus print it (R733).
 *
 * The figure is derived from the live world clock, not read off the wire:
 * the Sieges frame arrives on three edges only, so `g.windowInSec` is a
 * snapshot that can sit still for hours. The wire field stays as the
 * cross-check (state.windowInSec mirrors hub/objects.go).
 *
 * The colour of the open case belongs to this text, not to the call sites:
 * an open window is an alarm in both menus, so one rule states it once and
 * the callers write the result into the markup.
 */
export function windowText(s: GameState, g: SiegeInfo): string {
  const inSec = s.windowInSec(g.windowHour);
  return inSec < 0
    ? `<span style="color:var(--bad)">${S.structures_window_open}</span>`
    : S.structures_window({ hour: pad2(g.windowHour), left: dur(inSec) });
}

export function el(tag: string, cls = '', id = ''): HTMLDivElement {
  const e = document.createElement(tag) as HTMLDivElement;
  if (cls) e.className = cls;
  if (id) e.id = id;
  return e;
}

// --- the build card's preview (R1081, ui-map-interface.md §8) ---
//
// The server computes BOTH ends of every figure a building level drives,
// and it ships the unlock ladder in the hello catalog. What is left here is
// the sentence: one label for each figure, one form for each value. The
// client keeps no rule table, and nothing below computes a game figure.

/**
 * The words of one figure a building level drives: the label of the build
 * card, and the short key of line 2 of a build row (R1847), the condensed
 * form of round 087 card 04, "alloy 108 → 146/h".
 */
const EFFECT_WORDS: Partial<Record<LevelEffectKind, readonly [label: string, key: string]>> = {
  [LevelEffectKind.LEVEL_EFFECT_BASE_CAP]: [S.eff_base_cap, S.brief_base_cap],
  [LevelEffectKind.LEVEL_EFFECT_RESEARCH_SPEED]: [S.eff_research_speed, S.brief_research_speed],
  [LevelEffectKind.LEVEL_EFFECT_BUILD_SLOTS]: [S.eff_build_slots, S.brief_build_slots],
  [LevelEffectKind.LEVEL_EFFECT_QUEUE_SPEED]: [S.eff_queue_speed, S.brief_queue_speed],
  [LevelEffectKind.LEVEL_EFFECT_MASTER_SLOTS]: [S.eff_master_slots, S.brief_master_slots],
  [LevelEffectKind.LEVEL_EFFECT_COPY_SLOTS]: [S.eff_copy_slots, S.brief_copy_slots],
  [LevelEffectKind.LEVEL_EFFECT_PRINT_LISTINGS]: [S.eff_print_listings, S.brief_print_listings],
  [LevelEffectKind.LEVEL_EFFECT_SHIP_SPEED]: [S.eff_ship_speed, S.brief_ship_speed],
  [LevelEffectKind.LEVEL_EFFECT_COMBAT_CAP]: [S.eff_combat_cap, S.brief_combat_cap],
  [LevelEffectKind.LEVEL_EFFECT_SUPPORT_CAP]: [S.eff_support_cap, S.brief_support_cap],
  [LevelEffectKind.LEVEL_EFFECT_STORAGE_CAP]: [S.eff_storage_cap, S.brief_storage_cap],
  [LevelEffectKind.LEVEL_EFFECT_ALLOY_RATE]: [S.eff_alloy_rate, S.brief_alloy_rate],
  [LevelEffectKind.LEVEL_EFFECT_BIOMASS_RATE]: [S.eff_biomass_rate, S.brief_biomass_rate],
  [LevelEffectKind.LEVEL_EFFECT_VOLATILES_RATE]: [S.eff_volatiles_rate, S.brief_volatiles_rate],
  [LevelEffectKind.LEVEL_EFFECT_MISSION_CAP]: [S.eff_mission_cap, S.brief_mission_cap],
  [LevelEffectKind.LEVEL_EFFECT_SONAR_RANGE]: [S.eff_sonar_range, S.brief_sonar_range],
  [LevelEffectKind.LEVEL_EFFECT_DIVE_RISK]: [S.eff_dive_risk, S.brief_dive_risk],
  [LevelEffectKind.LEVEL_EFFECT_ISOTOPE_LOTS]: [S.eff_isotope_lots, S.brief_isotope_lots],
  [LevelEffectKind.LEVEL_EFFECT_BASE_TUBES]: [S.eff_base_tubes, S.brief_base_tubes],
  [LevelEffectKind.LEVEL_EFFECT_TORPEDO_STORAGE]: [S.eff_torpedo_storage, S.brief_torpedo_storage],
  [LevelEffectKind.LEVEL_EFFECT_SHIP_ROUND_STORAGE]: [S.eff_ship_round_storage, S.brief_ship_round_storage],
  [LevelEffectKind.LEVEL_EFFECT_CAPTAIN_SLOTS]: [S.eff_captain_slots, S.brief_captain_slots],
  [LevelEffectKind.LEVEL_EFFECT_TRAINING_SPEED]: [S.eff_training_speed, S.brief_training_speed],
  [LevelEffectKind.LEVEL_EFFECT_HOSPITAL_HOURS]: [S.eff_hospital_hours, S.brief_hospital_hours],
  [LevelEffectKind.LEVEL_EFFECT_PRISON_CELLS]: [S.eff_prison_cells, S.brief_prison_cells],
  [LevelEffectKind.LEVEL_EFFECT_CAPTURE_RANGE]: [S.eff_capture_range, S.brief_capture_range],
  [LevelEffectKind.LEVEL_EFFECT_CAPTURE_CHANCE]: [S.eff_capture_chance, S.brief_capture_chance],
  // The Depot (v2-the-deep.md §11.5, R2644).
  [LevelEffectKind.LEVEL_EFFECT_SEA_MINE_CAP]: [S.eff_sea_mine_cap, S.brief_sea_mine_cap],
  [LevelEffectKind.LEVEL_EFFECT_DEPLOYABLE_CAP]: [S.eff_deployable_cap, S.brief_deployable_cap],
  [LevelEffectKind.LEVEL_EFFECT_PALISADE_CAP]: [S.eff_palisade_cap, S.brief_palisade_cap],
  [LevelEffectKind.LEVEL_EFFECT_LANE_LIGHT_CAP]: [S.eff_lane_light_cap, S.brief_lane_light_cap],
  [LevelEffectKind.LEVEL_EFFECT_SEA_OBJECT_RACK]: [S.eff_sea_object_rack, S.brief_sea_object_rack],
};

/** The label of one figure a building level drives, or undefined for a
 *  kind the card states no row for. The level picker reads it (R1839). */
export function effectLabel(kind: LevelEffectKind): string | undefined {
  return EFFECT_WORDS[kind]?.[0];
}

/** The four figures that read as a multiplier of a rate. */
const EFFECT_MUL: readonly LevelEffectKind[] = [
  LevelEffectKind.LEVEL_EFFECT_RESEARCH_SPEED,
  LevelEffectKind.LEVEL_EFFECT_QUEUE_SPEED,
  LevelEffectKind.LEVEL_EFFECT_SHIP_SPEED,
  LevelEffectKind.LEVEL_EFFECT_TRAINING_SPEED,
];

/**
 * One figure, in the form its kind reads in. A multiplier keeps two
 * decimals, because one Research Lab level moves the second one. The dive
 * risk and the Brig's capture chance are shares and read as a percentage
 * (R1488). A rest is a count of hours, and it keeps one decimal only
 * when the pack sets a part of an hour. Everything else is a count, and the label carries the
 * unit, so the value is the grouped figure alone (R687).
 */
export function effectValue(kind: LevelEffectKind, v: number): string {
  if (EFFECT_MUL.includes(kind)) return S.eff_mul({ n: v.toFixed(2) });
  if (kind === LevelEffectKind.LEVEL_EFFECT_DIVE_RISK
    || kind === LevelEffectKind.LEVEL_EFFECT_CAPTURE_CHANCE) {
    return S.eff_pct({ n: Math.round(v * 100) });
  }
  if (kind === LevelEffectKind.LEVEL_EFFECT_HOSPITAL_HOURS) {
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
  }
  return groupThousands(Math.round(v));
}

/** The change of one figure with its sign: `+3` or `−0.05×`. */
function effectChange(e: LevelEffect): string {
  const d = e.next - e.now;
  return (d < 0 ? '−' : '+') + effectValue(e.kind, Math.abs(d));
}

/**
 * One row of the effect block of a build card (R1081, R1826): the label,
 * the figure now, the figure the level of the BUILD press buys, and the
 * change with its sign. The change carries its sign, so a figure that
 * FALLS (the dive risk, the hours a wounded captain rests) reads as plainly
 * as one that rises. The row never says "better": it says what the figure
 * is. Null when the figure takes no step: a maxed building sends no
 * `next`, and an unset `next` decodes as 0, which is also a figure a
 * level can buy, so `maxed` must come from the BuildingLevel. The card
 * then states the figure as a fact (`effectParts`).
 */
export function effectRow(e: LevelEffect, maxed: boolean): EffectRow | null {
  const label = EFFECT_WORDS[e.kind]?.[0];
  if (!label || maxed || e.next === e.now) return null;
  return {
    key: label,
    now: esc(effectValue(e.kind, e.now)),
    after: esc(effectValue(e.kind, e.next)),
    delta: esc(effectChange(e)),
  };
}

/**
 * The label and the value markup of one preview row, for a panel that
 * draws the row in its own frame: CAPTAIN · BRIG states the Brig's figures
 * as the build card states them (R1570). Null for a kind with no label.
 */
export function effectParts(e: LevelEffect, maxed: boolean): { label: string; value: string } | null {
  const label = EFFECT_WORDS[e.kind]?.[0];
  if (!label) return null;
  const now = effectValue(e.kind, e.now);
  if (maxed || e.next === e.now) return { label, value: esc(now) };
  const step = S.eff_step({ now, next: effectValue(e.kind, e.next) });
  const change = S.eff_change({ n: effectChange(e) });
  return { label, value: `${esc(step)} <span class="ichg">${esc(change)}</span>` };
}


/** The unit a short key leaves to the figure: the long label carries it
 *  ("alloy an hour"), the short key does not ("alloy … /h"). */
const EFFECT_UNIT: Partial<Record<LevelEffectKind, (p: { n: string }) => string>> = {
  [LevelEffectKind.LEVEL_EFFECT_ALLOY_RATE]: S.brief_per_hour,
  [LevelEffectKind.LEVEL_EFFECT_BIOMASS_RATE]: S.brief_per_hour,
  [LevelEffectKind.LEVEL_EFFECT_VOLATILES_RATE]: S.brief_per_hour,
  [LevelEffectKind.LEVEL_EFFECT_SONAR_RANGE]: S.brief_px,
  [LevelEffectKind.LEVEL_EFFECT_CAPTURE_RANGE]: S.brief_px,
  [LevelEffectKind.LEVEL_EFFECT_HOSPITAL_HOURS]: S.brief_hours,
};

/**
 * The condensed preview of the first figure of a building level as line 2
 * of its build row prints it (R1847, R1081): the short key, the figure now
 * and the figure after with its unit, with no change, for example
 * `alloy 108 → 146/h`. A maxed building states the figure now with its
 * unit. Escaped markup; the figure after wears `<em>`. Empty for a
 * building whose figures carry no key.
 */
export function effectBrief(effects: readonly LevelEffect[], maxed: boolean): string {
  const e = effects.find((x) => EFFECT_WORDS[x.kind] !== undefined);
  if (!e) return '';
  const key = esc(EFFECT_WORDS[e.kind]![1]);
  const unit = EFFECT_UNIT[e.kind] ?? ((p: { n: string }) => p.n);
  const now = effectValue(e.kind, e.now);
  if (maxed || e.next === e.now) return `${key} ${esc(unit({ n: now }))}`;
  const next = esc(unit({ n: effectValue(e.kind, e.next) }));
  return `${key} ${S.eff_step({ now: esc(now), next: `<em>${next}</em>` })}`;
}

/** The words of one rung, or a sentence that takes the rung's figure. */
type RungWords = string | ((p: { n: number }) => string);

/**
 * What each rung of an unlock ladder opens, in two forms: the short form
 * of line 2 of a build row (R1873), 13 characters or fewer like a short
 * key, and the sentence of the build card (R1081). The Shipyard rung has
 * no row: it names its hull (`unlockWords`).
 */
const UNLOCK_WORDS: Partial<Record<LevelUnlockKind, readonly [brief: RungWords, text: RungWords]>> = {
  [LevelUnlockKind.LEVEL_UNLOCK_BUILD_SLOT]: [S.brief_opens_build_slot, S.opens_build_slot],
  [LevelUnlockKind.LEVEL_UNLOCK_TECH_TIER]: [S.brief_opens_tech_tier, S.opens_tech_tier],
  [LevelUnlockKind.LEVEL_UNLOCK_RECV_WARSHIP]: [S.brief_opens_recv_warship, S.opens_recv_warship],
  [LevelUnlockKind.LEVEL_UNLOCK_RECV_INSTALLATION]: [S.brief_opens_recv_installation, S.opens_recv_installation],
  [LevelUnlockKind.LEVEL_UNLOCK_SHARE_FLEET]: [S.brief_opens_share_fleet, S.opens_share_fleet],
  [LevelUnlockKind.LEVEL_UNLOCK_ORACLE_SHARE]: [S.brief_opens_oracle_share, S.opens_oracle_share],
  [LevelUnlockKind.LEVEL_UNLOCK_RECV_FLEET]: [S.brief_opens_recv_fleet, S.opens_recv_fleet],
  [LevelUnlockKind.LEVEL_UNLOCK_BASE_TUBES]: [S.brief_opens_base_tubes, S.opens_base_tubes],
  [LevelUnlockKind.LEVEL_UNLOCK_POINT_DEFENCE]: [S.brief_opens_point_defence, S.opens_point_defence],
  [LevelUnlockKind.LEVEL_UNLOCK_WEAPON_TYPES]: [S.brief_opens_weapon_types, S.opens_weapon_types],
  [LevelUnlockKind.LEVEL_UNLOCK_SHIELD_TYPES]: [S.brief_opens_shield_types, S.opens_shield_types],
  [LevelUnlockKind.LEVEL_UNLOCK_MODULE_SLOT]: [S.brief_opens_module_slot, S.opens_module_slot],
};

/**
 * What one rung opens, in the reader's language: `form` 0 is the short
 * form of a build row, 1 the sentence of the build card. The hull the
 * Shipyard level lays down reads as its name in both: the catalog
 * already names it, so the client mirrors no roster; a Depot rung names
 * its kind of sea object the same way. A rung this build
 * has no words for reads as ''.
 */
function unlockWords(s: GameState, u: LevelUnlock, form: 0 | 1): string {
  if (u.kind === LevelUnlockKind.LEVEL_UNLOCK_SHIPYARD_HULL) return s.ship(u.value as ShipClass)?.name ?? '';
  // A Depot rung names the kind of sea object it opens (R2653), from the
  // catalog row, as a Shipyard rung names its hull.
  if (u.kind === LevelUnlockKind.LEVEL_UNLOCK_SEA_OBJECT) return s.seaObject(u.value as SeaObjectKind)?.name ?? '';
  const w = UNLOCK_WORDS[u.kind]?.[form];
  if (w === undefined) return '';
  return typeof w === 'string' ? w : w({ n: u.value });
}

/**
 * Line 2 of a building row (R1847, amended by R1873): the condensed effect
 * of the level the BUILD button buys, as `effectBrief` writes it. A building
 * whose levels move no figure, the Signal Station, states what that level
 * opens instead, `opens fleet share`, with the rung in `<em>`. A maxed
 * building, or a level that opens nothing, states the last rung it holds
 * alone, and nothing when it holds none. Escaped markup.
 */
export function buildingBrief(
  s: GameState, effects: readonly LevelEffect[], rungs: readonly LevelUnlock[],
  level: number, queued: number, maxed: boolean,
): string {
  const fig = effectBrief(effects, maxed);
  if (fig) return fig;
  const next = level + queued + 1;
  const opens = maxed ? undefined
    : rungs.map((u) => (u.level === next ? unlockWords(s, u, 0) : '')).find((t) => t !== '');
  if (opens) return S.brief_opens({ what: `<em>${esc(opens)}</em>` });
  let held = '';
  for (const u of rungs) {
    const t = u.level <= level ? unlockWords(s, u, 0) : '';
    if (t) held = t;
  }
  return esc(held);
}

/**
 * The rungs of the unlock ladder of a build card, in level order: the level
 * at the left and what it opens at the right (R1081). A rung this empire
 * already holds wears the `done` mark, and a rung above it wears `locked`,
 * so the player reads how far up the ladder the empire stands.
 */
export function unlockFacts(s: GameState, rungs: readonly LevelUnlock[], level: number): Fact[] {
  const out: Fact[] = [];
  for (const u of rungs) {
    const text = unlockWords(s, u, 1);
    if (!text) continue;
    out.push({
      key: S.info_opens_at({ level: u.level }),
      val: `<span class="${level >= u.level ? 'done' : 'locked'}">${esc(text)}</span>`,
    });
  }
  return out;
}

